import { TimelineOverlay, TimelineLayout, TimelineOverlayDatumContentProducer, TimelineOverlayOptions, TimelineOverlayContentProducerOptions, TimelineOverlayCategory } from '../timeline/timeline-overlay';
import { getPaletteColor, colorToString, adjustColor } from '../util/color';
import { Map as TLMDMap } from '../map/map';
import { Subscription } from 'rxjs';
import * as dataUtil from '../data-source/util';
import { Datum, MetaData } from '../data-source/types';
import { Visualizer, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { Drawer } from '../panel/cabinets';
import { delegatedObserver } from '../dom/dom';
import { ModelingDataSource } from './modeling';
import { getImpactScoreColor, ModelProject } from './modeling-common';
import { ModelingVisualizerControl, ModelingContainer } from './container-modeling';
import { CombineMode, ScalarFieldManagerStatusIndicator, OverlayDistribution } from '../worker/scalar-field-manager';

export class ModelingTimelineVisualizer extends TimelineOverlay implements Visualizer, ScalarFieldManagerStatusIndicator {
	private controls: { [key: string]: ModelingVisualizerControl }
	private inspectionContainer: ModelingContainer
	private source: ModelingDataSource;
	private cacheSubscription: Subscription;

	private combineMode: CombineMode;
	private projectsToRender: string[];


	private static CLASS_LOAD_INDICATOR = 'tlmd-modeling-load-indicator';
	private static CLASS_CREATION_INDICATOR = 'tlmd-modeling-creation-indicator';

	protected draw: () => any;

	public constructor(private visualizerKey: string, private mdlType: string, private drawer: Drawer, className: string) {

		super(
			TimelineOverlayCategory.MODELLING_PROJECT,
			className,
			{
				eventHandlers: {
					'click': (evAndTrgt, data) => {
						evAndTrgt.event.preventDefault();
						//						console.log('event=%o', evAndTrgt.event);
						//						console.log('target=%o', evAndTrgt.target);
						//						console.log('data=%o', data);
						let overlayItem = (<TimelineOverlayDatumContentProducer>this.getProducer()).getItem(evAndTrgt.target.id);
						if (overlayItem) {
							drawer.getContainer().getApplication().getInspectionPanel().switchContainer(overlayItem.group).then(_containerPanel => {
								drawer.getContainer().getDataSource().onSelection(overlayItem.group, overlayItem.item.dataItemIds);
							});
						}
					}
				}
			},
			new TimelineOverlayDatumContentProducer(
				{
					size: () => { return { width: 0, height: 15, border: 1 }; },
					draw: (domElem, meta, size, animationScript, millisToLocalPx) => {
						// dataItem0 is first modelproject
						let dataItem0: any = meta.dataItems[meta.dataItemIds[0]];
						// set bar color
						let barBaseColor = getPaletteColor(0, dataItem0.parameters.render.hue);

						domElem.style.backgroundColor = colorToString(adjustColor(barBaseColor, { hue: 0, saturation: 0, lightness: 0, alpha: -0.5 }));
						domElem.style.borderColor = colorToString(adjustColor(barBaseColor, { hue: 0, saturation: 0, lightness: (barBaseColor.lightness * 0.5), alpha: 0 }));

						domElem.style.borderWidth = `${size.border}px`;

						// add div for a description and the number of underlying projects
						let description = dataItem0.parameters.description + (meta.dataItemIds.length > 1 ? ` ( + ${meta.dataItemIds.length - 1} )` : '');
						domElem.innerHTML = `<div class="tlmd-marker-label" style="left:${meta.visiblePX.start.local}px; width:${meta.visiblePX.end.local - meta.visiblePX.start.local - (2 * size.border)}px;"><div>${description}</div></div>`;

						if (animationScript && animationScript.length > 1) {

							let scriptInterval = animationScript[1].center - animationScript[0].center;


							let impactStatusMarkup: string[] = animationScript.map((animationInstant, index) => {
								//						let left = millisToLocalPx(animationInstant.center - (scriptInterval/2));
								//						let right = millisToLocalPx(animationInstant.center + (scriptInterval/2));
								let left = index > 0 ? millisToLocalPx(animationInstant.center) : meta.visiblePX.start.local;
								let right = index < animationScript.length - 1 ? millisToLocalPx(animationInstant.center + scriptInterval) : meta.visiblePX.end.local;
								//return `<div class="_${animationInstant.center}" style="background-color:${index % 2 ? '#000' : '#fff'}; color:${index % 2 ? '#fff' : '#000'}; position:absolute; height:30%; bottom:0; left:${left}px; width:${right - left}px;">${index}</div>`
								return `<div class="_${animationInstant.center}" style="background-color:#666; position:absolute; height:30%; bottom:0; left:${left}px; width:${right - left}px;"></div>`
							});
							domElem.insertAdjacentHTML('afterbegin', impactStatusMarkup.join(''));
						}
					}
				},
				{
					isMoment: (datum: any) => false,
					getTime: (datum: any) => undefined,
					getStart: (datum: any) => datum.start,
					getEnd: (datum: any) => datum.end,
					compareByTime: dataUtil.compareByTime,
					temporalHash: dataUtil.datumTemporalHash
				},
				{
					layout: TimelineLayout.stack,
					padding: 3,
					marginGroup: 3,
					marginRow: 2
				}));

		this.inspectionContainer = <ModelingContainer>this.drawer.getContainer();
		this.source = this.inspectionContainer.getDataSource();
		this.source.getScalarFieldManager(this.mdlType).setStatusIndicator(this);

		let self = this;
		this.draw = (): any => {
			//is this the firstime draw?
			if (self.getLayer().childElementCount === 0) {
				//add svg creation indicator based on animation script
				self.getLayer().appendChild(self.buildDIVElem(ModelingTimelineVisualizer.CLASS_CREATION_INDICATOR));
				//add load progress indicator based on profile
				self.getLayer().appendChild(self.buildDIVElem(ModelingTimelineVisualizer.CLASS_LOAD_INDICATOR));
			}

			let drawResult = super.draw();

			self.setImpacts();

			return drawResult;
		}

		this.cacheSubscription = this.source.getScalarFieldManager(this.mdlType).getCacheObservable().subscribe(_loadedCacheKeys => {
			//test if we the viewer is active
			if (this.controls && this.controls[this.type()] && this.controls[this.type()].enabled(this.key())) {
				//update impact status leds
				let combineMode = this.controls[this.type()].combineMode(this.key());
				this.setImpacts();
			}
		});

		//this.subscribeHandlers();

	}

	private setImpacts(): void {
		//		let combineMode = this.controls[this.type()].combineMode(this.key());
		if (this.combineMode) {
			let dataList = this.source.getData()[this.type()];
			let groups = dataList.getOverlayElementIds(this.combineMode);

			groups.forEach(elemId => {
				let groupName = '_' + elemId;
				this.source.getData()[this.type()].getAnimationKeys().forEach(animationTimestamp => {
//					let impacts = this.source.getScalarFieldManager().getImpacts(animationTimestamp, this.combineMode, elemId);
const debugTime0 =new Date(Date.parse('2024-11-27T03:40:00Z'));
const debugTime1 =new Date(Date.parse('2024-11-27T03:20:00Z'));
const debug = debugTime0.getTime() === animationTimestamp ||  debugTime1.getTime() === animationTimestamp;
const debugTime = debug ? new Date(animationTimestamp) : undefined;



					let impacts = this.source.getScalarFieldManager(this.mdlType).getImpacts(animationTimestamp, elemId);
					let bgColor = 'transparent';
					if (impacts) {
						if (impacts.highestGoi) {
							let highestGoi = impacts.gois[impacts.highestGoi];
							if (highestGoi && highestGoi.highestBand) {
								let highestBand = highestGoi.bands[highestGoi.highestBand];
								if (highestBand && highestBand.score && highestBand.score.combined != undefined) {
//									highestBand.score.combined
if(debug && highestBand.score.combined > 0){
	console.log(`getImpactScoreColor(${highestBand.score.combined})  for  ${debugTime.toISOString()} yields`, getImpactScoreColor( highestBand.score.combined));
	
}
									bgColor = highestBand.score.combined > 0.0 ? colorToString( getImpactScoreColor( highestBand.score.combined) ) : 'transparent';
								}
							}
						}

						let led = <HTMLDivElement>this.getLayer().querySelector(`div.${groupName}>div._${animationTimestamp}`);
						if (led) {
							led.style.backgroundColor = bgColor;
						} else {
							//console.log('no LED found for %s %s %s', groupName, animationTimestamp, new Date(animationTimestamp).toISOString());
						}
					}
				})
			});
		}
	}

	public changeLayout(options: TimelineOverlayContentProducerOptions): void {
		(<TimelineOverlayDatumContentProducer>this.getProducer()).setOptions(options);
		this.update();
	}

	public getProducer(): TimelineOverlayDatumContentProducer {
		return <TimelineOverlayDatumContentProducer>super.getProducer();
	}

	public updateLoadingStatus(loadingProfile: { start: number, end: number, available: boolean }[]) {
		this.updateStatus(ModelingTimelineVisualizer.CLASS_LOAD_INDICATOR, loadingProfile);
	}

	public updateCreationStatus(creationProfile: { start: number, end: number, available: boolean }[]) {
		this.updateStatus(ModelingTimelineVisualizer.CLASS_CREATION_INDICATOR, creationProfile);
	}

	private updateStatus(indicatorClassName: string, statusProfile: { start: number, end: number, available: boolean }[], statusColor?: string) {
		if (this.getLayer()) {
			let indicatorDiv = <HTMLDivElement>this.getLayer().querySelector(`div.${indicatorClassName}`);
			if (indicatorDiv && statusProfile && statusProfile.length > 0) {
				const start = statusProfile[0].start;
				const end = statusProfile[statusProfile.length - 1].end;
				const projection = this.getProjection();
				const left = projection.fromMillisToPoint(start);
				const millisToLocalPixel = (millis: number): number => projection.fromMillisToPoint(millis) - left;
				const width = millisToLocalPixel(end);
				indicatorDiv.style.left = `${left}px`;
				indicatorDiv.style.width = `${width}px`;

				const unavailable = statusProfile.filter(status => !status.available);
				const unavailableClasses = unavailable.map(status => `${status.start}`);
				//remove all children that are available
				Array.from(indicatorDiv.children).filter(elem => unavailableClasses.indexOf(elem.className) === -1).forEach(elem => elem.parentElement.removeChild(elem));
				//add all children that are unavailable
				//compile a list of present statusDivs
				const present = Array.from(indicatorDiv.children).map(elem => elem.className);
				//filter all unavailable that are already present in the list of children and add to indicatorDiv
				unavailable.filter(status => present.indexOf(`${status.start}`) === -1).forEach(status => {
					let statusDiv = this.buildDIVElem(`_${status.start}`);
					const left = millisToLocalPixel(status.start);
					const width = millisToLocalPixel(status.end) - left;
					statusDiv.style.left = `${left}px`;
					statusDiv.style.width = `${width}px`;
					if (statusColor) statusDiv.style.backgroundColor = statusColor;
					indicatorDiv.appendChild(statusDiv);
				});

			}
		}
	}
	private buildDIVElem(className: string): HTMLElement {
		let buildingSite = document.createElement('DIV');
		buildingSite.innerHTML = `<div class="tlmd-overlay-marker ${className}" style="height:100%;"></div>`;
		let elem = <HTMLElement>buildingSite.firstElementChild;
		return elem;
	}

	public key(): string {
		return this.visualizerKey + '_' + this.type();
	}

	public type(): string {
		return this.mdlType;
	}

	public setControls(controls: { [key: string]: VisualizerControl }): Visualizer {
		this.controls = <{ [key: string]: ModelingVisualizerControl }>controls;
		return this;
	}

	public release(): void {
	}

	public setMap(_map: TLMDMap): void {
		//we do not deal with the map here
	}

	public updateSelection(): void {
		(<TimelineOverlayDatumContentProducer>this.getProducer()).highlight(this.getLayer());
	}

	public refresh(level: VisualizationUpdateLevel): void {
		let combineMode;
		let projectsToRender;
		if (this.controls && this.controls[this.type()] && this.controls[this.type()].enabled(this.key())) {
			combineMode = this.controls[this.type()].combineMode(this.key());
			projectsToRender = this.controls[this.type()].projectsToRender();
		}
		this.updateTimeline(level, combineMode, projectsToRender);
	}

	public updateTimeline(level: VisualizationUpdateLevel, combineMode: CombineMode, projectsToRender: string[]): void {//        level: VisualizationUpdateLevel): void {
		this.combineMode = combineMode;
		this.projectsToRender = projectsToRender;
		
		//test if we the viewer is active
		if (level != undefined && this.combineMode) {

			if (level <= VisualizationUpdateLevel.filter) {
				let dataList = this.source.getData()[this.type()];
				let modelProjects = <ModelProject[]>dataList.getData();
				if( this.projectsToRender ){
				modelProjects = modelProjects.filter( mp => this.projectsToRender.includes(mp.id))  ;
				}
				let categoryMap = dataList.getCategorizedModelProjects();
				

				
				
//				let overlayDistributions = this.source.getScalarFieldManager().getOverlayDistributions(modelProjects, categoryMap, this.combineMode);
				let overlayDistributions = this.source.getScalarFieldManager(this.mdlType).getOverlayDistributions(modelProjects, categoryMap);

				//group model projects as determined by the combine setting
				let projectCategoryMap: { [key: string]: { list: Datum[], getData: () => Datum[], getMeta: () => MetaData } } = {};

				let overlayElementMap = overlayDistributions[this.combineMode];

				Object.keys(overlayElementMap).forEach(elemId => {
					let groupList: Datum[] = [];
					let groupDataProvider: { list: Datum[], getData: () => Datum[], getMeta: () => MetaData } = {
						list: groupList,
						getData: () => groupList,
						getMeta: () => {
							return {
								start: undefined,
								end: undefined,
								north: undefined,
								east: undefined,
								south: undefined,
								west: undefined,
								version: undefined,
								lastUpdate: Date.now(),
								selected: []
							};
						}
					};
					projectCategoryMap['_' + elemId] = groupDataProvider;

					overlayElementMap[elemId].dataIds.forEach(modelProjectId => {
						let modelProject = <ModelProject>dataList.get(modelProjectId);
						groupDataProvider.list.push(modelProject);
					})

				});

				(<TimelineOverlayDatumContentProducer>this.getProducer()).setAnimationPosition(this.source.getTimeline().getCursorMillis(true));
				(<TimelineOverlayDatumContentProducer>this.getProducer()).setData(projectCategoryMap);
				(<TimelineOverlayDatumContentProducer>this.getProducer()).setLayout(this.combineMode === CombineMode.none ? TimelineLayout.stack : TimelineLayout.combine);

				if (!this.getTimeline()) {
					this.setTimeline(this.source.getTimeline());
				}
				this.update();
			}
		} else {
			//visualizer not active 
			this.setTimeline(null);
		}
	}
	//	private subscribeHandlers(): void {
	//			'div.tlmd-content.settings .tlmd-viewer-settings.map .tlmd-foldout input',
	//
	//		let self = this;
	//
	//		let timelineSettingsDiv = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.settings .tlmd-viewer-settings.timeline');
	//
	//		this.inspectionContainer.addSubscription(
	//			delegatedObserver(timelineSettingsDiv, 'change', '.tlmd-foldout.timeline-viewer .tlmd-radio-group.combine input')
	//				.subscribe(evAndTrgt => {
	//					evAndTrgt.event.preventDefault();
	//					self.refresh(VisualizationUpdateLevel.settings);
	//				})
	//		);
	//
	//	}

}